import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PiCheckBold, PiXBold, PiLockKeyBold, PiCaretLeftBold } from "react-icons/pi";
import "../nocturne.css";
import "../admin.css";
import { ONBOARDING_INVITEE, ROLE_SCOPE, STAFF_ROLES, STAFF_ROLE_LABEL } from "../data/mockData";

const STEPS = ["Password", "Two-factor", "Welcome"];

/** Password rules, evaluated live. Mirrors the rule set the main app already
 *  enforces in AccountCreation so staff and customer flows agree. */
const rulesFor = (pw) => [
  { label: "At least 8 characters", ok: pw.length >= 8 },
  { label: "One uppercase letter", ok: /[A-Z]/.test(pw) },
  { label: "One lowercase letter", ok: /[a-z]/.test(pw) },
  { label: "One number", ok: /[0-9]/.test(pw) },
  { label: "One special character (!@#$%^&*)", ok: /[!@#$%^&*]/.test(pw) },
];

/**
 * Staff onboarding — the three-step flow an invited admin completes.
 *
 * Standalone page, deliberately outside AdminLayout: someone part-way through
 * setup has no account yet, so rendering a sidebar full of sections they cannot
 * reach would be wrong. Same reasoning the main app already applies to
 * PersonaPicker. Nothing is submitted anywhere.
 */
const StaffOnboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");

  const rules = useMemo(() => rulesFor(password), [password]);
  const passwordValid = rules.every((r) => r.ok);
  const roleLabel = STAFF_ROLE_LABEL[ONBOARDING_INVITEE.role];
  const roleBlurb = STAFF_ROLES.find((r) => r.value === ONBOARDING_INVITEE.role).blurb;

  const next = () => {
    setError("");
    if (step === 0) {
      if (!passwordValid) return setError("Your password doesn't meet all the requirements yet.");
      if (password !== confirm) return setError("The two passwords don't match.");
    }
    if (step === 1) {
      if (!/^\d{6}$/.test(code)) return setError("Enter the 6-digit code from your authenticator app.");
    }
    setStep((s) => s + 1);
  };

  return (
    <div className="nocturne admin-onboard">
      <div className="card elev-md admin-onboard-card">
        <div className="admin-steps">
          {STEPS.map((label, i) => (
            <span key={label} className={`admin-step-bar ${i <= step ? "admin-step-bar--on" : ""}`} />
          ))}
        </div>

        <div className="card-kicker">
          Step {step + 1} of {STEPS.length} · {STEPS[step]}
        </div>

        {/* ---------------------------------------------------- step 1 --- */}
        {step === 0 && (
          <>
            <h3 style={{ margin: "0 0 2px" }}>Set your password</h3>
            <p className="card-body" style={{ flex: "none" }}>
              Welcome, {ONBOARDING_INVITEE.name.split(" ")[0]}. {ONBOARDING_INVITEE.invitedBy} invited
              you to the BuildBeta admin dashboard as <strong>{roleLabel}</strong>.
            </p>

            <div className="admin-field-list">
              <div className="field">
                <label htmlFor="ob-email">Work email</label>
                <input id="ob-email" className="input" value={ONBOARDING_INVITEE.email} readOnly disabled />
              </div>

              <div className="field">
                <label htmlFor="ob-pw">New password</label>
                <input
                  id="ob-pw"
                  className="input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Choose a strong password"
                />
                <div className="admin-rule-list">
                  {rules.map((r) => (
                    <div className="admin-rule" key={r.label} style={{ color: r.ok ? "var(--status-ok)" : "color-mix(in srgb, var(--color-text) 55%, transparent)" }}>
                      {r.ok ? <PiCheckBold /> : <PiXBold />} {r.label}
                    </div>
                  ))}
                </div>
              </div>

              <div className="field">
                <label htmlFor="ob-confirm">Confirm password</label>
                <input
                  id="ob-confirm"
                  className="input"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Type it again"
                />
              </div>
            </div>
          </>
        )}

        {/* ---------------------------------------------------- step 2 --- */}
        {step === 1 && (
          <>
            <h3 style={{ margin: "0 0 2px" }}>Set up two-factor</h3>
            <p className="card-body" style={{ flex: "none" }}>
              Admin accounts reach customer records and financial data, so two-factor
              authentication is required — it cannot be skipped.
            </p>

            <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
              <div className="admin-qr">QR code<br />placeholder</div>
              <div style={{ flex: 1 }}>
                <div className="admin-section-label">Can't scan?</div>
                <p className="card-body" style={{ fontSize: 12, flex: "none" }}>
                  Enter this setup key in your authenticator app instead.
                </p>
                <div className="admin-check" style={{ letterSpacing: "0.12em" }}>
                  JBSW Y3DP EHPK 3PXP
                </div>
              </div>
            </div>

            <div className="field">
              <label htmlFor="ob-code">6-digit verification code</label>
              <input
                id="ob-code"
                className="input admin-mfa-code"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
              />
            </div>
          </>
        )}

        {/* ---------------------------------------------------- step 3 --- */}
        {step === 2 && (
          <>
            <h3 style={{ margin: "0 0 2px" }}>You're all set</h3>
            <p className="card-body" style={{ flex: "none" }}>
              Your account is active with two-factor enabled. Here's what your{" "}
              <strong>{roleLabel}</strong> role can reach.
            </p>

            <div className="admin-check" style={{ color: "var(--status-ok)" }}>
              <PiLockKeyBold /> Two-factor authentication enabled
            </div>

            <div>
              <div className="admin-section-label">{roleLabel} — {roleBlurb}</div>
              <div className="admin-scope-list">
                {ROLE_SCOPE[ONBOARDING_INVITEE.role].map((s) => (
                  <div className="admin-scope" key={s}>
                    <PiCheckBold style={{ color: "var(--status-ok)", flex: "none" }} /> {s}
                  </div>
                ))}
              </div>
              <p className="card-meta" style={{ marginTop: 10 }}>
                Sections outside this list stay hidden. Ask an Owner if you need them.
              </p>
            </div>
          </>
        )}

        {error && <div style={{ fontSize: 12, color: "var(--status-danger)" }}>{error}</div>}

        <div className="dialog-actions">
          {step > 0 && step < 2 && (
            <button type="button" className="btn btn-secondary" onClick={() => { setError(""); setStep((s) => s - 1); }}>
              <PiCaretLeftBold /> Back
            </button>
          )}
          {step < 2 ? (
            <button type="button" className="btn btn-primary" onClick={next}>
              {step === 0 ? "Continue" : "Verify & continue"}
            </button>
          ) : (
            <button type="button" className="btn btn-primary" onClick={() => navigate("/admin")}>
              Go to dashboard
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffOnboarding;
